import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AccountTree,
  Assignment,
  CreateNewFolder,
  Description,
  DriveFileMove,
  DriveFileRenameOutline,
  Folder,
} from '@mui/icons-material';

import { IDocument } from '../../Interfaces/IDocument';
import { useDocumentTransfer } from './DocumentTransferContext';
import {
  DOCUMENT_MOVE_GROUP_LABEL,
  DocumentMoveGroup,
  IDocumentLocation,
  IDocumentMoveTarget,
  isSameDocumentLocation,
} from './documentTransferTypes';

interface IProps {
  documentObject: IDocument;
  /** Fundstelle des Dokuments; ohne Angabe werden keine Aktionen gezeigt. */
  location?: IDocumentLocation;
  size?: 'small' | 'medium';
}

interface IHookProps {
  /** `null`, solange kein Dokument gewaehlt ist (z. B. Menue noch nie geoeffnet). */
  documentObject: IDocument | null;
  location?: IDocumentLocation;
  /** Wird beim Oeffnen eines Dialogs gerufen, um das Menue zu schliessen. */
  onOpenDialog?: () => void;
}

export interface IDocumentTransferActions {
  /** Steht die Funktion hier zur Verfuegung? Ausserhalb eines Vorgangs nicht. */
  isAvailable: boolean;
  /**
   * Die Dialoge. Muessen **ausserhalb** eines Menues gerendert werden — sonst
   * werden sie mit dem Menue unmountet und schliessen sich sofort wieder.
   */
  dialogs: React.ReactNode;
  /** Die beiden Menuepunkte fuer ein Drei-Punkte-Menue. */
  menuItems: React.ReactNode;
  openMove: (event: React.MouseEvent) => void;
  openRename: (event: React.MouseEvent) => void;
}

/**
 * Verschieben und Umbenennen eines Dokuments — in jedem Bereich des Vorgangs
 * verfuegbar. Ausserhalb eines Vorgangs (z. B. in Vorlagen) liefert der Hook
 * `isAvailable: false`, weil dann kein `DocumentTransferProvider` existiert.
 */
export const useDocumentTransferActions = (
  props: IHookProps
): IDocumentTransferActions => {
  const documentTransfer = useDocumentTransfer();

  const [isOpenMove, setIsOpenMove] = useState(false);
  const [isOpenRename, setIsOpenRename] = useState(false);
  const [fileName, setFileName] = useState('');
  const [isOpenNewDirectory, setIsOpenNewDirectory] = useState(false);
  const [newDirectoryTitle, setNewDirectoryTitle] = useState('');
  const [newDirectoryParentId, setNewDirectoryParentId] = useState<number | ''>(
    ''
  );

  const location = props.location;

  const targetArray = useMemo((): IDocumentMoveTarget[] => {
    if (documentTransfer === null || location === undefined) {
      return [];
    }

    return documentTransfer.targetArray.filter(
      target => !isSameDocumentLocation(target.location, location)
    );
  }, [documentTransfer, location]);

  const isAvailable =
    documentTransfer !== null &&
    location !== undefined &&
    props.documentObject !== null;

  /** Nie `null`, wenn `isAvailable` — spart Fallunterscheidungen im Rendern. */
  const documentObject = (props.documentObject ?? {
    idDocument: -1,
    FileName: '',
    idMIMEType: 1,
    Created_at: '',
    idDocumentDirectory: null,
    idLegalTransaction: null,
    perEmail: false,
    isFromTemplate: false,
  }) as IDocument;

  const handleMove = async (target: IDocumentMoveTarget) => {
    if (documentTransfer === null || location === undefined) {
      return;
    }

    await documentTransfer.moveDocument(
      documentObject,
      location,
      target.location
    );
    setIsOpenMove(false);
  };

  const openMove = (event: React.MouseEvent) => {
    event.stopPropagation();
    setIsOpenMove(true);

    if (props.onOpenDialog) {
      props.onOpenDialog();
    }
  };

  const openRename = (event: React.MouseEvent) => {
    event.stopPropagation();
    setFileName(documentObject.FileName);
    setIsOpenRename(true);

    if (props.onOpenDialog) {
      props.onOpenDialog();
    }
  };

  const handleRename = () => {
    if (
      fileName.trim() === '' ||
      documentTransfer === null ||
      location === undefined
    ) {
      return;
    }

    documentTransfer.renameDocument(documentObject, location, fileName.trim());
    setIsOpenRename(false);
  };

  /** Neuen Standardordner anlegen und das Dokument direkt dorthin verschieben. */
  const handleCreateDirectoryAndMove = async () => {
    if (
      newDirectoryTitle.trim() === '' ||
      documentTransfer === null ||
      location === undefined
    ) {
      return;
    }

    const idDocumentDirectory = documentTransfer.createDirectory(
      newDirectoryTitle.trim(),
      newDirectoryParentId === '' ? null : newDirectoryParentId
    );

    await documentTransfer.moveDocument(documentObject, location, {
      kind: 'directory',
      idDocumentDirectory: idDocumentDirectory,
    });

    setIsOpenNewDirectory(false);
    setNewDirectoryTitle('');
    setIsOpenMove(false);
  };

  const renderTarget = (target: IDocumentMoveTarget, key: string) => {
    const Icon =
      target.group === 'directory'
        ? Folder
        : target.group === 'requirement'
          ? Assignment
          : Description;

    return (
      <ListItemButton
        key={key}
        onClick={() => handleMove(target)}
        sx={{ borderRadius: 1, pl: 2 + (target.depth ?? 0) * 2.5 }}
      >
        <ListItemIcon sx={{ minWidth: 34 }}>
          <Icon fontSize='small' color='action' />
        </ListItemIcon>
        <ListItemText primary={target.label} secondary={target.hint} />
      </ListItemButton>
    );
  };

  /** Ein Abschnitt des Dialogs; leere Abschnitte zeigen einen Hinweis. */
  const renderGroup = (
    group: DocumentMoveGroup,
    emptyHint: string,
    content: React.ReactNode,
    isEmpty: boolean
  ) => (
    <React.Fragment key={`move-group-${group}`}>
      <ListSubheader disableSticky sx={{ px: 0, lineHeight: '32px' }}>
        {DOCUMENT_MOVE_GROUP_LABEL[group]}
      </ListSubheader>
      {isEmpty ? (
        <Typography
          variant='body2'
          color='text.secondary'
          sx={{ pl: 2, pb: 1 }}
        >
          {emptyHint}
        </Typography>
      ) : (
        content
      )}
      <Divider sx={{ my: 1 }} />
    </React.Fragment>
  );

  const areaTargetArray = targetArray.filter(target => target.group === 'area');
  const requirementTargetArray = targetArray.filter(
    target => target.group === 'requirement'
  );
  const directoryTargetArray = targetArray.filter(
    target => target.group === 'directory'
  );

  /** Phasen in der Reihenfolge, in der ihre Maßnahmen geliefert werden. */
  const phaseArray = Array.from(
    new Set(
      requirementTargetArray.map(target => target.subGroup ?? 'Ohne Phase')
    )
  );

  const dialogs = (
    <>
      <Dialog
        open={isOpenMove}
        onClose={() => setIsOpenMove(false)}
        maxWidth='md'
        fullWidth
        onClick={(event: React.MouseEvent) => event.stopPropagation()}
      >
        <DialogTitle>
          Dokument verschieben
          <Typography variant='body2' color='text.secondary'>
            {documentObject.FileName} — aktuell in{' '}
            {location === undefined
              ? ''
              : (documentTransfer?.getLocationLabel(location) ?? '')}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity='info' sx={{ mb: 2 }}>
            Die Änderung wird erst mit dem Speichern des Vorgangs übernommen.
            Die Datei selbst bleibt unverändert. Beim Ziel „Rechnungen" werden
            QR-Code und Urkundennummer geprüft.
          </Alert>
          {documentTransfer?.isCheckingInvoice === true && (
            <Alert
              severity='info'
              icon={<CircularProgress size={18} />}
              sx={{ mb: 2 }}
            >
              QR-Code &amp; Urkundennummer werden gelesen …
            </Alert>
          )}
          <List dense disablePadding>
            {renderGroup(
              'area',
              'Es steht kein weiterer Bereich zur Verfügung.',
              areaTargetArray.map((target, index) =>
                renderTarget(target, `move-area-${index}`)
              ),
              areaTargetArray.length === 0
            )}

            {renderGroup(
              'directory',
              'In der internen Datenablage ist noch kein Ordner vorhanden — legen Sie unten einen an.',
              directoryTargetArray.map((target, index) =>
                renderTarget(target, `move-directory-${index}`)
              ),
              directoryTargetArray.length === 0
            )}

            {/* Neuer Ordner direkt aus dem Dialog — er bekommt eine temporaere
                ID und wird mit dem Vorgang gespeichert. */}
            {isOpenNewDirectory ? (
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ sm: 'center' }}
                sx={{ pl: 2, pb: 1, mt: -1 }}
              >
                <TextField
                  label='Ordnername'
                  value={newDirectoryTitle}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    setNewDirectoryTitle(event.target.value)
                  }
                  size='small'
                  autoFocus
                  inputProps={{ maxLength: 255 }}
                  sx={{ minWidth: 220 }}
                />
                <TextField
                  label='Übergeordneter Ordner'
                  value={newDirectoryParentId}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    setNewDirectoryParentId(
                      event.target.value === ''
                        ? ''
                        : Number(event.target.value)
                    )
                  }
                  size='small'
                  select
                  sx={{ minWidth: 220 }}
                >
                  <MenuItem value=''>— oberste Ebene —</MenuItem>
                  {(documentTransfer?.directoryArray ?? []).map(entry => (
                    <MenuItem
                      key={`new-directory-parent-${entry.idDocumentDirectory}`}
                      value={entry.idDocumentDirectory}
                    >
                      {`${'  '.repeat(entry.depth)}${entry.Title}`}
                    </MenuItem>
                  ))}
                </TextField>
                <Button
                  variant='contained'
                  size='small'
                  disabled={newDirectoryTitle.trim() === ''}
                  onClick={handleCreateDirectoryAndMove}
                >
                  Anlegen und verschieben
                </Button>
                <Button
                  variant='text'
                  size='small'
                  onClick={() => setIsOpenNewDirectory(false)}
                >
                  Abbruch
                </Button>
              </Stack>
            ) : (
              <Button
                size='small'
                startIcon={<CreateNewFolder />}
                sx={{ ml: 1, mb: 1, mt: -1 }}
                onClick={() => {
                  setNewDirectoryTitle('');
                  setNewDirectoryParentId('');
                  setIsOpenNewDirectory(true);
                }}
              >
                Neuen Ordner anlegen
              </Button>
            )}
            <Divider sx={{ my: 1 }} />

            {renderGroup(
              'requirement',
              'Für diesen Vorgang sind keine Maßnahmen angelegt.',
              phaseArray.map(phase => (
                <React.Fragment key={`move-phase-${phase}`}>
                  <ListItem sx={{ py: 0.25, pl: 1 }}>
                    <ListItemIcon sx={{ minWidth: 34 }}>
                      <AccountTree fontSize='small' color='primary' />
                    </ListItemIcon>
                    <ListItemText
                      primary={phase}
                      primaryTypographyProps={{
                        variant: 'subtitle2',
                        color: 'text.primary',
                      }}
                    />
                  </ListItem>
                  {requirementTargetArray
                    .filter(
                      target => (target.subGroup ?? 'Ohne Phase') === phase
                    )
                    .map((target, index) =>
                      renderTarget(target, `move-requirement-${phase}-${index}`)
                    )}
                </React.Fragment>
              )),
              requirementTargetArray.length === 0
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button variant='outlined' onClick={() => setIsOpenMove(false)}>
            Abbruch
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isOpenRename}
        onClose={() => setIsOpenRename(false)}
        maxWidth='xs'
        fullWidth
        onClick={(event: React.MouseEvent) => event.stopPropagation()}
      >
        <DialogTitle>Datei umbenennen</DialogTitle>
        <DialogContent>
          <Alert severity='info' sx={{ mb: 2 }}>
            Eine Änderung der Dateiendung kann zu einer Beschädigung der Datei
            führen.
          </Alert>
          <TextField
            label='Dateiname'
            value={fileName}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setFileName(event.target.value)
            }
            size='small'
            fullWidth
            autoFocus
            inputProps={{ maxLength: 255 }}
          />
        </DialogContent>
        <DialogActions>
          <Button variant='outlined' onClick={() => setIsOpenRename(false)}>
            Abbruch
          </Button>
          <Button
            variant='contained'
            disabled={fileName.trim() === ''}
            onClick={handleRename}
          >
            Übernehmen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  const menuItems = (
    <>
      <MenuItem onClick={openMove}>
        <ListItemIcon>
          <DriveFileMove fontSize='small' />
        </ListItemIcon>
        <ListItemText>Verschieben</ListItemText>
      </MenuItem>
      <MenuItem onClick={openRename}>
        <ListItemIcon>
          <DriveFileRenameOutline fontSize='small' />
        </ListItemIcon>
        <ListItemText>Umbenennen</ListItemText>
      </MenuItem>
    </>
  );

  return {
    isAvailable: isAvailable,
    dialogs: isAvailable ? dialogs : null,
    menuItems: isAvailable ? menuItems : null,
    openMove: openMove,
    openRename: openRename,
  };
};

/**
 * Verschieben und Umbenennen als zwei Icon-Buttons — fuer Stellen ohne
 * Drei-Punkte-Menue.
 */
export const DocumentTransferActions: React.FC<IProps> = props => {
  const actions = useDocumentTransferActions({
    documentObject: props.documentObject,
    location: props.location,
  });

  if (!actions.isAvailable) {
    return null;
  }

  const size = props.size ?? 'medium';

  return (
    <>
      {actions.dialogs}
      <Tooltip title='Verschieben'>
        <IconButton size={size} onClick={actions.openMove}>
          <DriveFileMove fontSize={size === 'small' ? 'small' : 'medium'} />
        </IconButton>
      </Tooltip>
      <Tooltip title='Umbenennen'>
        <IconButton size={size} onClick={actions.openRename}>
          <DriveFileRenameOutline
            fontSize={size === 'small' ? 'small' : 'medium'}
          />
        </IconButton>
      </Tooltip>
    </>
  );
};
